import React, { useEffect, useMemo, useState } from "react";

import type { ColumnsType } from "antd/es/table";

import Chip from "../../../shared/Chip/Chip";
import CustomPagination from "../../../shared/CustomPagination/CustomPagination";
import DataTableBase from "../../../shared/dashboards/DataTable/DataTable";
import RebetLoader from "../../../CommonComponents/RebetLoader/RebetLoader";
import { BRAND_ICONS_URLS } from "../../../../utils/Constants";
import freeCashIcon from "../../../../assets/images/free-cash.png";

import styles from "./SGPIdBetsList.module.css";
import type {
  SgpIdBetRow,
  SgpIdBetStatus,
} from "./sgpIdBetsListData";
import {
  resolveSgpIdBetAmountIcon,
  type SgpIdBetAmountColumn,
} from "./sgpIdBetsListAmountIcon";
import {
  SGP_RISK_BETS_LIST_SORT_FIELDS,
  type SgpRiskBetsListSortBy,
} from "../../../../queries/sportsbook/sgpRiskBetsList";
import {
  compareRowsByField,
  normalizeSorter,
  resolveServerSortChange,
  type SortField,
  type SortOrder,
} from "./sgpIdBetsListTableSort";

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50];
const amountFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const CASH_ICON_SRC = `${
  process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL ?? ""
}${BRAND_ICONS_URLS.primaryCurrencySymbol}`;
const AMOUNT_ICON_SRC = {
  cash: CASH_ICON_SRC,
  freeCash: freeCashIcon,
} as const;
const SERVER_SORTABLE_FIELDS = new Set(Object.keys(SGP_RISK_BETS_LIST_SORT_FIELDS));
const SERVER_SORTER = { compare: () => 0 };
const API_TO_TABLE_SORT_FIELDS = Object.fromEntries(
  Object.entries(SGP_RISK_BETS_LIST_SORT_FIELDS).map(([tableKey, apiField]) => [
    apiField,
    tableKey,
  ]),
) as Record<SgpRiskBetsListSortBy, keyof typeof SGP_RISK_BETS_LIST_SORT_FIELDS>;

const DataTable = DataTableBase as React.ComponentType<{
  id?: string;
  rowKey?: string | ((record: SgpIdBetRow) => string);
  columns?: ColumnsType<SgpIdBetRow>;
  dataSource?: SgpIdBetRow[];
  pagination?: boolean | object;
  className?: string;
  rowClassName?: string;
  onRow?: (record: SgpIdBetRow) => Record<string, unknown>;
  onChange?: (...args: unknown[]) => void;
}>;

function getPlayerInitials(player: string): string {
  const parts = player
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length > 1) {
    return parts
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("");
  }

  return player.slice(0, 2).toUpperCase();
}

function PlayerCell({ player }: { player: string }) {
  return (
    <div className={styles.playerCell}>
      <span className={styles.playerAvatar} aria-hidden="true">
        {getPlayerInitials(player)}
      </span>
      <span className={styles.playerName}>{player}</span>
    </div>
  );
}

function SelectionCell({
  row,
  field,
}: {
  row: SgpIdBetRow;
  field: "label" | "value";
}) {
  return (
    <div className={styles.expandingCell}>
      {row.selections.map((selection) => (
        <span
          key={`${row.id}-${selection.label}-${field}`}
          className={styles.expandingLine}
        >
          {selection[field]}
        </span>
      ))}
    </div>
  );
}

type OddsTextVariant = "default" | "strikethrough" | "link";

function OddsText({
  children,
  variant = "default",
}: {
  children: React.ReactNode;
  variant?: OddsTextVariant;
}) {
  const variantClassNameMap = {
    default: styles.oddsTextDefault,
    strikethrough: styles.oddsTextStrikethrough,
    link: styles.oddsTextLink,
  } as const;

  return (
    <span className={`${styles.oddsText} ${variantClassNameMap[variant]}`}>
      {children}
    </span>
  );
}

function OddsCell({ row }: { row: SgpIdBetRow }) {
  return (
    <div className={styles.expandingCell}>
      {row.selections.map((selection) => (
        <OddsText
          key={`${row.id}-${selection.label}-odds`}
          variant={selection.voided ? "strikethrough" : "default"}
        >
          {selection.odds}
        </OddsText>
      ))}
      <OddsText variant="link">{row.overallOddsDisplay}</OddsText>
    </div>
  );
}

function BetResultCell({ row }: { row: SgpIdBetRow }) {
  const resultClassNameMap = {
    Won: styles.resultWon,
    Lost: styles.resultLost,
    Push: styles.resultPush,
    Pending: styles.resultPending,
  } as const;
  const resultDotClassNameMap = {
    Won: styles.resultDotWon,
    Lost: styles.resultDotLost,
    Push: styles.resultDotPush,
    Pending: styles.resultDotPending,
  } as const;

  return (
    <div className={styles.expandingCell}>
      {row.selections.map((selection) => (
        <span
          key={`${row.id}-${selection.label}-result`}
          className={`${styles.result} ${resultClassNameMap[selection.result]}`}
        >
          <span
            className={`${styles.resultDot} ${resultDotClassNameMap[selection.result]}`}
            aria-hidden="true"
          />
          {selection.result}
        </span>
      ))}
    </div>
  );
}

function AmountCell({
  amount,
  testId,
  moneyType,
  column,
}: {
  amount: number;
  testId: string;
  moneyType?: string;
  column: SgpIdBetAmountColumn;
}) {
  const iconKind = resolveSgpIdBetAmountIcon(moneyType, column);
  const iconSrc = iconKind ? AMOUNT_ICON_SRC[iconKind] : null;

  return (
    <div className={styles.amountCell} data-testid={testId}>
      {iconSrc ? (
        <img
          src={iconSrc}
          alt=""
          aria-hidden="true"
          className={styles.amountIcon}
          data-testid={`${testId}-icon`}
        />
      ) : null}
      <span className={styles.amountValue}>{amountFormatter.format(amount)}</span>
    </div>
  );
}

function getStatusTone(status: SgpIdBetStatus) {
  switch (status) {
    case "Won":
      return "success" as const;
    case "Pending":
      return "warning" as const;
    case "Push":
      return "neutral" as const;
    case "Lost":
    default:
      return "danger" as const;
  }
}

function StatusCell({ status }: { status: SgpIdBetStatus }) {
  return (
    <div className={styles.statusCell} data-testid="bet-status-cell">
      <Chip tone={getStatusTone(status)}>{status}</Chip>
    </div>
  );
}

export interface SGPIdBetsListTableProps {
  rows: SgpIdBetRow[];
  totalRows?: number;
  currentPage?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  defaultPageSize?: number;
  itemLabel?: string;
  sortBy?: SgpRiskBetsListSortBy;
  sortOrder?: "asc" | "desc";
  onSortChange?: (nextSort: {
    sortBy: SgpRiskBetsListSortBy;
    sortOrder: "asc" | "desc";
  }) => void;
  loading?: boolean;
}

function SGPIdBetsListTable({
  rows,
  totalRows,
  currentPage: controlledCurrentPage,
  pageSize: controlledPageSize,
  onPageChange,
  onPageSizeChange,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  itemLabel = "bets",
  sortBy = "created_at",
  sortOrder = "desc",
  onSortChange,
  loading = false,
}: SGPIdBetsListTableProps) {
  const [internalCurrentPage, setInternalCurrentPage] = useState(1);
  const [internalPageSize, setInternalPageSize] = useState(defaultPageSize);
  const [internalSortField, setInternalSortField] = useState<SortField | null>(null);
  const [internalSortOrder, setInternalSortOrder] = useState<SortOrder>(null);

  const isServerMode = totalRows != null;
  const currentPage = controlledCurrentPage ?? internalCurrentPage;
  const pageSize = controlledPageSize ?? internalPageSize;
  const activeSortKey = onSortChange
    ? API_TO_TABLE_SORT_FIELDS[sortBy]
    : internalSortField;
  const activeSortOrder = onSortChange
    ? sortOrder === "asc"
      ? "ascend"
      : "descend"
    : internalSortOrder;

  useEffect(() => {
    if (!isServerMode) {
      setInternalCurrentPage(1);
    }
  }, [rows, pageSize, isServerMode]);

  const sortedRows = useMemo(() => {
    if (onSortChange || !internalSortField || !internalSortOrder) {
      return rows;
    }

    const nextRows = [...rows].sort((left, right) => {
      const result = compareRowsByField(internalSortField, left, right);
      return internalSortOrder === "ascend" ? result : -result;
    });

    return nextRows;
  }, [rows, internalSortField, internalSortOrder, onSortChange]);

  const paginatedRows = useMemo(() => {
    if (isServerMode) {
      return rows;
    }

    const start = (currentPage - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [rows, sortedRows, currentPage, pageSize, isServerMode]);

  const totalCount = isServerMode ? totalRows : rows.length;

  const columns: ColumnsType<SgpIdBetRow> = useMemo(() => {
    const withSorter = (
      columnKey: SortField,
      column: ColumnsType<SgpIdBetRow>[number],
    ) => {
      if (onSortChange) {
        if (!SERVER_SORTABLE_FIELDS.has(columnKey)) {
          return column;
        }

        return {
          ...column,
          sorter: SERVER_SORTER,
          sortOrder: activeSortKey === columnKey ? activeSortOrder : null,
          showSorterTooltip: false,
        };
      }

      return {
        ...column,
        sorter: true,
        sortOrder: internalSortField === columnKey ? internalSortOrder : null,
        showSorterTooltip: false,
      };
    };

    return [
      {
        title: "ID",
        dataIndex: "id",
        key: "id",
        width: 120,
      },
      withSorter("player", {
        title: "Player",
        dataIndex: "player",
        key: "player",
        width: 180,
        render: (_value: string, record: SgpIdBetRow) => (
          <PlayerCell player={record.player} />
        ),
      }),
      withSorter("dateTime", {
        title: "Date&Time",
        dataIndex: "dateTime",
        key: "dateTime",
        width: 220,
      }),
      withSorter("sport", {
        title: "Sport",
        dataIndex: "sport",
        key: "sport",
        width: 120,
      }),
      withSorter("league", {
        title: "League",
        dataIndex: "league",
        key: "league",
        width: 120,
      }),
      {
        title: "SGP ID",
        dataIndex: "sgpId",
        key: "sgpId",
        width: 260,
        render: (value: string) => (
          <span className={styles.sgpId} title={value}>
            {value}
          </span>
        ),
      },
      {
        title: "Selection",
        key: "selection",
        width: 260,
        className: styles.expandingColumn,
        render: (_value: unknown, record: SgpIdBetRow) => (
          <SelectionCell row={record} field="value" />
        ),
      },
      withSorter("odds", {
        title: "Odds",
        key: "odds",
        width: 130,
        className: styles.expandingColumn,
        render: (_value: unknown, record: SgpIdBetRow) => <OddsCell row={record} />,
      }),
      {
        title: "Bet Result",
        key: "betResult",
        width: 160,
        className: styles.expandingColumn,
        render: (_value: unknown, record: SgpIdBetRow) => (
          <BetResultCell row={record} />
        ),
      },
      withSorter("betAmount", {
        title: "Bet Amount",
        dataIndex: "betAmount",
        key: "betAmount",
        width: 130,
        render: (_value: unknown, record: SgpIdBetRow) => (
          <AmountCell
            amount={record.betAmount}
            testId="bet-amount-cell"
            moneyType={record.moneyType}
            column="betAmount"
          />
        ),
      }),
      withSorter("status", {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 110,
        render: (value: SgpIdBetStatus) => <StatusCell status={value} />,
      }),
      withSorter("payout", {
        title: "Payout",
        dataIndex: "winAmount",
        key: "payout",
        width: 130,
        render: (_value: unknown, record: SgpIdBetRow) => (
          <AmountCell
            amount={record.winAmount}
            testId="bet-payout-cell"
            moneyType={record.moneyType}
            column="payout"
          />
        ),
      }),
    ];
  }, [activeSortKey, activeSortOrder, internalSortField, internalSortOrder, onSortChange]);

  const handlePageSizeChange = (nextPageSize: number) => {
    if (onPageSizeChange) {
      onPageSizeChange(nextPageSize);
      return;
    }

    setInternalPageSize(nextPageSize);
    setInternalCurrentPage(1);
  };

  const handlePageChange = (nextPage: number) => {
    if (onPageChange) {
      onPageChange(nextPage);
      return;
    }

    setInternalCurrentPage(nextPage);
  };

  const handleTableChange = (
    _pagination: unknown,
    _filters: unknown,
    sorter: unknown,
  ) => {
    if (onSortChange) {
      const sortChange = resolveServerSortChange({
        sorter,
        currentSortBy: sortBy,
        currentSortOrder: sortOrder,
      });

      if (sortChange.type === "reset" || sortChange.type === "update") {
        onSortChange({
          sortBy: sortChange.sortBy,
          sortOrder: sortChange.sortOrder,
        });
      }

      return;
    }

    const nextSorter = normalizeSorter(sorter);
    setInternalSortField(nextSorter.field);
    setInternalSortOrder(nextSorter.order);
    setInternalCurrentPage(1);
  };

  return (
    <div className={styles.table} data-testid="sgp-id-bets-list-table">
      <div className={styles.tableBody}>
        {loading ? (
          <div
            className={styles.tableLoader}
            data-testid="sgp-id-bets-list-table-loader"
          >
            <RebetLoader height="100px" width="100px" />
          </div>
        ) : (
          <DataTable
            id="sgp-id-bets-list-table"
            rowKey="id"
            columns={columns}
            dataSource={paginatedRows}
            pagination={false}
            onChange={handleTableChange}
            onRow={(record) => ({
              "data-testid": `sgp-id-bets-list-row-${record.id}`,
            })}
          />
        )}
      </div>
      {!loading && (
        <div className={styles.tablePagination}>
          <CustomPagination
            total={totalCount}
            pageSize={pageSize}
            currentPage={currentPage}
            onPageSizeChange={handlePageSizeChange}
            onPageChange={handlePageChange}
            itemLabel={itemLabel}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
          />
        </div>
      )}
    </div>
  );
}

export default SGPIdBetsListTable;
